import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Users, MousePointerClick, Target, Smartphone, Monitor, Tablet, MapPin, RefreshCw, ExternalLink, AlertCircle } from "lucide-react";

const PERIODS = [
  { v: 1, l: "Last 24h" },
  { v: 7, l: "Last 7 days" },
  { v: 30, l: "Last 30 days" },
  { v: 90, l: "Last 90 days" },
];

export default function TrafficPanel() {
  const [period, setPeriod] = useState(7);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get(`/platform/traffic?period_days=${period}`);
      setData(data);
    } catch (e) { setErr(e); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  return (
    <div className="space-y-6" data-testid="traffic-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-display font-black uppercase tracking-tight text-2xl">Site traffic</h2>
        <div className="flex gap-2">
          <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
            <SelectTrigger className="rounded-sm border-zinc-400 w-[160px]" data-testid="traffic-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.v} value={String(p.v)}>{p.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={load} disabled={busy} className="rounded-sm uppercase tracking-wider font-bold text-xs">
            <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {err && (
        <div className="border-2 border-red-600 bg-red-50 dark:bg-red-900/10 p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-700 dark:text-red-400 shrink-0 mt-0.5" />
          <div className="text-sm font-bold break-words">{err.response?.data?.detail || err.message}</div>
        </div>
      )}

      {data && (
        <>
          {/* Top tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile icon={MousePointerClick} label="Page views" value={data.total_views?.toLocaleString() || 0} accent="bg-[#09090B]" />
            <Tile icon={Users} label="Unique visitors" value={data.unique_visitors?.toLocaleString() || 0} accent="bg-[#FF5F15]" />
            <Tile icon={TrendingUp} label="Signups" value={data.signups?.toLocaleString() || 0} accent="bg-green-600" />
            <Tile icon={Target} label="Conversion" value={`${(data.conversion_rate * 100).toFixed(2)}%`} accent="bg-blue-600" />
          </div>

          {/* Trend chart */}
          <TrendChart byDay={data.by_day || []} signups={data.signups_series || []} />

          <div className="grid lg:grid-cols-2 gap-4">
            <ListTable
              title="Top pages"
              icon={MousePointerClick}
              rows={(data.top_pages || []).map((r) => ({ key: r.path, label: r.path, value: r.views, sub: `${r.uniques} unique` }))}
              testId="traffic-top-pages"
            />
            <ListTable
              title="Top referrers"
              icon={ExternalLink}
              rows={(data.top_referrers || []).map((r) => ({ key: r.host || "direct", label: r.host || "(direct)", value: r.n }))}
              empty="No referrers yet — most traffic is direct or organic search."
              testId="traffic-top-referrers"
            />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <BreakdownTile title="Devices" icon={Smartphone} rows={data.devices || []} keyField="device" />
            <BreakdownTile title="Browsers" icon={Monitor} rows={data.browsers || []} keyField="browser" />
            <BreakdownTile title="Countries" icon={MapPin} rows={data.countries || []} keyField="country" empty="No country data — set up Cloudflare in front of the deploy to populate this." />
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent }) {
  return (
    <div className="border-2 border-zinc-900 dark:border-zinc-700 bg-white dark:bg-[#151C27] p-3">
      <div className={`w-7 h-7 ${accent} text-white flex items-center justify-center mb-2`}><Icon className="w-3.5 h-3.5" /></div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display font-black text-2xl leading-none mt-1">{value}</div>
    </div>
  );
}

function TrendChart({ byDay, signups }) {
  const max = useMemo(() => Math.max(1, ...byDay.map((d) => d.views || 0)), [byDay]);
  const signupMax = useMemo(() => Math.max(1, ...signups.map((d) => d.n || 0)), [signups]);
  const signupsByDate = useMemo(() => {
    const m = {};
    signups.forEach((s) => { m[s.date] = s.n; });
    return m;
  }, [signups]);
  if (!byDay.length) {
    return <div className="border-2 border-zinc-300 dark:border-zinc-700 p-6 text-center text-zinc-500 text-sm">No traffic in this window yet.</div>;
  }
  return (
    <div className="border-2 border-zinc-900 dark:border-zinc-700 p-4" data-testid="traffic-trend-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Page views per day</div>
        <div className="flex gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#FF5F15]"></span> Views</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-600"></span> Signups</span>
        </div>
      </div>
      <div className="flex items-end gap-1 h-32">
        {byDay.map((d) => {
          const h = Math.round(((d.views || 0) / max) * 100);
          const sCount = signupsByDate[d.date] || 0;
          const sh = Math.round((sCount / signupMax) * 100);
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5 min-w-0" title={`${d.date}: ${d.views} views, ${d.uniques} uniques, ${sCount} signups`}>
              <div className="w-full flex items-end gap-0.5 h-full">
                <div className="flex-1 bg-[#FF5F15]" style={{ height: `${Math.max(2, h)}%` }} />
                {sCount > 0 && <div className="flex-1 bg-green-600" style={{ height: `${Math.max(4, sh)}%` }} />}
              </div>
              <div className="text-[8px] font-mono uppercase tracking-widest text-zinc-500 truncate w-full text-center">{d.date.slice(5)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListTable({ title, icon: Icon, rows, testId, empty }) {
  return (
    <div className="border-2 border-zinc-300 dark:border-zinc-700 bg-white dark:bg-[#151C27]" data-testid={testId}>
      <div className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#FF5F15]" />
        <span className="text-[10px] font-bold uppercase tracking-widest">{title}</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-xs text-zinc-500">{empty || "Nothing yet."}</div>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          {rows.map((r, i) => (
            <div key={r.key + i} className="px-3 py-2 flex items-center gap-2 border-t border-zinc-100 dark:border-zinc-800">
              <span className="text-xs font-mono truncate flex-1 min-w-0">{r.label}</span>
              <span className="text-[10px] font-mono text-zinc-500 shrink-0">{r.sub || ""}</span>
              <span className="font-bold tabular-nums shrink-0">{r.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BreakdownTile({ title, icon: Icon, rows, keyField, empty }) {
  const total = rows.reduce((acc, r) => acc + (r.n || 0), 0);
  return (
    <div className="border-2 border-zinc-300 dark:border-zinc-700 bg-white dark:bg-[#151C27]">
      <div className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#FF5F15]" />
        <span className="text-[10px] font-bold uppercase tracking-widest">{title}</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-xs text-zinc-500">{empty || "Nothing yet."}</div>
      ) : (
        <div className="p-3 space-y-2">
          {rows.map((r) => {
            const pct = total ? ((r.n / total) * 100).toFixed(1) : 0;
            return (
              <div key={r[keyField] || "unknown"} className="text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-mono uppercase tracking-wider">{r[keyField] || "unknown"}</span>
                  <span className="font-bold tabular-nums">{r.n.toLocaleString()} <span className="text-zinc-500 font-normal">({pct}%)</span></span>
                </div>
                <div className="w-full h-1 bg-zinc-200 dark:bg-zinc-800 mt-1">
                  <div className="h-1 bg-[#FF5F15]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
